import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toPng } from "html-to-image";
import { auth, db } from "./firebase";
import { doc, getDoc } from "firebase/firestore";
import PageLayout from "./components/PageLayout";
import BuildQRCode from "./components/BuildQRCode";
import logoLight from "./assets/logo2.png";
import logoDark from "./assets/logo3.png";
import qrLogo from "./assets/mybuildid_qr_logo_black_red_transparent.png";
import {
  VEHICLE_SPEC_FIELDS,
  getCurrentStage,
  normalizeCarStages,
  normalizeVehicleSpecs
} from "./utils/buildStages";
import { calculateContainScale } from "./utils/displaySizing";

const PORTRAIT_BOARD_WIDTH = 850;
const PORTRAIT_BOARD_HEIGHT = 1100;
const LANDSCAPE_BOARD_WIDTH = 1100;
const LANDSCAPE_BOARD_HEIGHT = 675;

const transparentPixelDataUrl =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==";

const getAssetSrc = (asset) =>
  asset?.startsWith("/") ? asset : `/${String(asset || "").replace(/^\.?\//, "")}`;

const logoLightSrc = getAssetSrc(logoLight);
const logoDarkSrc = getAssetSrc(logoDark);

function ShowDisplay() {
  const { username, carId } = useParams();
  const navigate = useNavigate();
  const displayRef = useRef(null);
  const boardRef = useRef(null);
  const previewViewportRef = useRef(null);
  const generatedAtRef = useRef(new Date());

  const [authResolved, setAuthResolved] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [car, setCar] = useState(null);
  const [pageError, setPageError] = useState("");
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState("dark");
  const [layout, setLayout] = useState("portrait");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSavingImage, setIsSavingImage] = useState(false);
  const [exportError, setExportError] = useState("");
  const [previewBounds, setPreviewBounds] = useState({ width: 0, height: 0 });
  const [settledImageSrc, setSettledImageSrc] = useState("");
  const [fontsReadyForCar, setFontsReadyForCar] = useState(null);

  const isLandscape = layout === "landscape";
  const boardWidth = isLandscape ? LANDSCAPE_BOARD_WIDTH : PORTRAIT_BOARD_WIDTH;
  const boardHeight = isLandscape ? LANDSCAPE_BOARD_HEIGHT : PORTRAIT_BOARD_HEIGHT;
  const previewScale = useMemo(() => calculateContainScale({
    containerWidth: previewBounds.width,
    containerHeight: isFullscreen ? previewBounds.height : Infinity,
    artworkWidth: boardWidth,
    artworkHeight: boardHeight,
    maxScale: 1
  }), [previewBounds, isFullscreen, boardWidth, boardHeight]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
      setAuthResolved(true);

      if (!user) {
        navigate("/auth", { replace: true });
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    const loadShowDisplay = async () => {
      if (!authResolved) return;

      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setPageError("");

        if (!username || !carId) {
          setPageError("Page unavailable.");
          return;
        }

        const profileRef = doc(db, "profiles", username.toLowerCase());
        const profileSnap = await getDoc(profileRef);

        if (!profileSnap.exists()) {
          setPageError("Page unavailable.");
          return;
        }

        const profileData = profileSnap.data();

        if (currentUser.uid !== profileData.userId) {
          navigate(`/user/${username}/car/${carId}`, { replace: true });
          return;
        }

        const foundCar = profileData.garageCars?.find(
          (item) => String(item.id) === String(carId)
        );

        if (!foundCar) {
          setPageError("Page unavailable.");
          return;
        }

        setProfile({
          ...profileData,
          username: profileData.username || username
        });
        setCar(normalizeCarStages(foundCar));
      } catch (error) {
        console.log("Show Display load failed:", error);
        setPageError("Page unavailable.");
      } finally {
        setLoading(false);
      }
    };

    loadShowDisplay();
  }, [authResolved, currentUser, username, carId, navigate]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  useLayoutEffect(() => {
    const updateBounds = () => {
      const viewport = previewViewportRef.current;

      if (!viewport) return;
      const rect = viewport.getBoundingClientRect();
      const previewPadding = isFullscreen ? 24 : 0;
      const nextBounds = { width: Math.max(rect.width - previewPadding, 0), height: Math.max(rect.height - previewPadding, 0) };
      setPreviewBounds((current) => Math.abs(current.width - nextBounds.width) < .5 && Math.abs(current.height - nextBounds.height) < .5 ? current : nextBounds);
    };

    updateBounds();

    let resizeObserver;

    if (typeof ResizeObserver !== "undefined" && previewViewportRef.current) {
      resizeObserver = new ResizeObserver(updateBounds);
      resizeObserver.observe(previewViewportRef.current);
    }

    window.addEventListener("resize", updateBounds);
    window.addEventListener("orientationchange", updateBounds);
    const frame = requestAnimationFrame(updateBounds);

    return () => {
      resizeObserver?.disconnect();
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", updateBounds);
      window.removeEventListener("orientationchange", updateBounds);
    };
  }, [isFullscreen, loading, car]);

  const currentPhase = useMemo(() => {
    if (!car) return null;
    return getCurrentStage(car);
  }, [car]);

  const vehicleTitle = useMemo(() => getVehicleTitle(car), [car]);

  const subtitle = useMemo(() => {
    return getVehicleSubtitle(car, vehicleTitle);
  }, [car, vehicleTitle]);

  const heroImage = useMemo(() => {
    const phaseImages = currentPhase?.images || [];
    const legacyImages = car?.images || [];

    return phaseImages[0] || legacyImages[0] || "";
  }, [car, currentPhase]);

  useEffect(() => {
    let active = true;

    const settleFonts = async () => {
      try {
        await document.fonts?.ready;
      } catch (error) {
        console.log("Show Display font readiness failed:", error);
      }

      if (active) setFontsReadyForCar(car);
    };

    settleFonts();

    return () => { active = false; };
  }, [car]);

  const vehicleSpecData = useMemo(() => {
    return getVehicleSpecComparisonData(car, currentPhase);
  }, [car, currentPhase]);

  const modBreakdown = useMemo(() => {
    return getModCategoryBreakdown(currentPhase?.mods || car?.mods || []);
  }, [car, currentPhase]);

  const stamp = useMemo(() => {
    return getShowDisplayStamp(profile?.username || username, generatedAtRef.current);
  }, [profile?.username, username]);

  const buildUrl = `${window.location.origin}/user/${username}/car/${carId}`;
  const activeTheme = theme === "light" ? lightTheme : darkTheme;
  const footerLogoSrc = theme === "light" ? logoLightSrc : logoDarkSrc;
  const isImageReady = !heroImage || settledImageSrc === heroImage;
  const isDisplayReady = previewScale > 0 && fontsReadyForCar === car && isImageReady;

  const handleFullscreen = async () => {
    try {
      if (!document.fullscreenElement && displayRef.current) {
        await displayRef.current.requestFullscreen();
      } else if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
    } catch (error) {
      console.log("Fullscreen failed:", error);
    }
  };

  const handleSaveImage = async () => {
    if (!boardRef.current || isSavingImage) return;

    try {
      setIsSavingImage(true);
      setExportError("");

      await waitForDocumentFonts();
      await waitForBoardImages(boardRef.current);
      await waitForNextFrame();
      await waitForNextFrame();

      const dataUrl = await withTimeout(
        toPng(boardRef.current, {
          cacheBust: false,
          pixelRatio: 2,
          width: boardWidth,
          height: boardHeight,
          backgroundColor: activeTheme.previewBackground,
          imagePlaceholder: transparentPixelDataUrl,
          skipAutoScale: false,
          style: {
            transform: "none",
            transformOrigin: "top left",
            margin: "0"
          }
        }),
        25000,
        "Image creation timed out."
      );

      downloadDataUrl(
        dataUrl,
        `${getSafeFileName(vehicleTitle)}-show-display.png`
      );
    } catch (error) {
      console.error("Show Display image export failed:", error);
      setExportError(
        error?.message ||
          "We couldn't save the Show Display image. Please refresh the page and try again."
      );
    } finally {
      setIsSavingImage(false);
    }
  };

  const stampElement = (
    <span
      aria-label={stamp.ariaLabel}
      title={stamp.title}
      style={{
        ...footerStampStyle,
        color: activeTheme.stampTextColor,
        borderColor: activeTheme.stampBorderColor,
        backgroundColor: activeTheme.stampBackground
      }}
    >
      {stamp.text}
    </span>
  );

  const heroContent = (
    <section style={heroSectionStyle}>
      {heroImage ? (
        <img
          src={heroImage}
          alt={vehicleTitle}
          style={{
            ...heroImageStyle,
            ...(isLandscape ? landscapeHeroImageStyle : {})
          }}
          onLoad={() => setSettledImageSrc(heroImage)}
          onError={() => setSettledImageSrc(heroImage)}
        />
      ) : (
        <div
          style={{
            ...heroPlaceholderStyle,
            ...(isLandscape ? landscapeHeroImageStyle : {}),
            color: activeTheme.mutedTextColor,
            borderColor: activeTheme.borderColor
          }}
        >
          MyBuildID
        </div>
      )}
    </section>
  );

  const footerContent = (
    <div style={isLandscape ? landscapeFooterGroupStyle : footerGroupStyle}>
      <div style={isLandscape ? landscapeFooterBrandColumnStyle : footerBrandColumnStyle}>
        <img
          src={footerLogoSrc}
          alt="MyBuildID"
          style={isLandscape ? landscapeFooterLogoStyle : footerLogoStyle}
        />

        <p
          style={{
            ...footerUrlStyle,
            color: activeTheme.mutedTextColor
          }}
        >
          mybuildid.com
        </p>

        {stampElement}
      </div>

      <div style={isLandscape ? landscapeFooterQrColumnStyle : footerQrColumnStyle}>
        <div style={qrPanelStyle}>
          <BuildQRCode
            url={buildUrl}
            logoSrc={qrLogo}
            size={isLandscape ? 168 : 150}
            theme={{
              backgroundColor: "#ffffff",
              dotColor: "#111111",
              cornerColor: "#e10600",
              logoColor: "#e10600",
              shape: "rounded",
              margin: 6,
              logoSize: 0.17
            }}
          />
        </div>

      </div>
    </div>
  );

  const specsSection = vehicleSpecData.rows.length > 0 ? (
    <section
      style={{
        ...displaySectionStyle,
        ...(isLandscape ? landscapeFirstRightSectionStyle : {}),
        borderColor: activeTheme.sectionBorderColor
      }}
    >
      <h2
        style={{
          ...sectionTitleStyle,
          color: activeTheme.accentColor
        }}
      >
        Vehicle Specs
      </h2>

      <div style={specTableStyle}>
        <div
          style={{
            ...specHeaderRowStyle,
            ...(isLandscape ? landscapeSpecGridStyle : portraitSpecGridStyle),
            color: activeTheme.mutedTextColor,
            borderColor: activeTheme.sectionBorderColor
          }}
        >
          <span>Spec</span>
          <span>OEM</span>
          <span>CURRENT SETUP</span>
        </div>

        {vehicleSpecData.rows.map((row) => {
          const isDifferent =
            row.oemValue &&
            row.currentValue &&
            row.oemValue !== row.currentValue;

          return (
            <div
              key={row.key}
              style={{
                ...specRowStyle,
                ...(isLandscape ? landscapeSpecGridStyle : portraitSpecGridStyle),
                borderColor: activeTheme.sectionBorderColor
              }}
            >
              <span
                style={{
                  ...specLabelStyle,
                  color: activeTheme.mutedTextColor
                }}
              >
                {row.label}
              </span>

              <span
                style={{
                  ...specOemValueStyle,
                  color: activeTheme.oemTextColor
                }}
              >
                {row.oemValue || "-"}
              </span>

              <span
                style={{
                  ...specCurrentValueStyle,
                  color: isDifferent
                    ? activeTheme.accentColor
                    : activeTheme.textColor
                }}
              >
                {row.currentValue || "-"}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  ) : null;

  const modBreakdownSection = modBreakdown.length > 0 ? (
    <section
      style={{
        ...displaySectionStyle,
        borderColor: activeTheme.sectionBorderColor
      }}
    >
      <h2
        style={{
          ...sectionTitleStyle,
          color: activeTheme.accentColor
        }}
      >
        Mod Breakdown
      </h2>

      <div style={breakdownStackStyle}>
        {modBreakdown.map((item, index) => (
          <div key={item.category} style={breakdownRowStyle}>
            <div style={breakdownLabelRowStyle}>
              <span>{item.category}</span>
              <strong>{item.percentage}%</strong>
            </div>

            <div
              style={{
                ...breakdownTrackStyle,
                backgroundColor: activeTheme.trackColor
              }}
            >
              <div
                style={{
                  ...breakdownFillStyle,
                  width: `${item.percentage}%`,
                  backgroundColor:
                    index === 0
                      ? activeTheme.accentColor
                      : activeTheme.secondaryBarColor
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </section>
  ) : null;

  if (loading || !authResolved) {
    return (
      <PageLayout maxWidth="920px" showBottomNav={false} showNavbar={false}>
        <div style={stateCardStyle}>Loading Show Display...</div>
      </PageLayout>
    );
  }

  if (pageError || !profile || !car) {
    return (
      <PageLayout maxWidth="920px" showBottomNav={false} showNavbar={false}>
        <div style={stateCardStyle}>Page unavailable.</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      maxWidth="1180px"
      showBack={!isFullscreen}
      showBottomNav={false}
      showNavbar={false}
      centered
    >
      {!isFullscreen && (
        <div style={controlsStyle}>
          <div style={controlGroupStyle} aria-label="Theme">
            <button
              type="button"
              onClick={() => setTheme("dark")}
              style={{
                ...controlButtonStyle,
                ...(theme === "dark" ? activeControlButtonStyle : {})
              }}
              aria-pressed={theme === "dark"}
            >
              Dark
            </button>

            <button
              type="button"
              onClick={() => setTheme("light")}
              style={{
                ...controlButtonStyle,
                ...(theme === "light" ? activeControlButtonStyle : {})
              }}
              aria-pressed={theme === "light"}
            >
              Light
            </button>
          </div>

          <div style={controlGroupStyle} aria-label="Layout">
            <button
              type="button"
              onClick={() => setLayout("portrait")}
              style={{
                ...controlButtonStyle,
                ...(layout === "portrait" ? activeControlButtonStyle : {})
              }}
              aria-pressed={layout === "portrait"}
            >
              Portrait
            </button>

            <button
              type="button"
              onClick={() => setLayout("landscape")}
              style={{
                ...controlButtonStyle,
                ...(layout === "landscape" ? activeControlButtonStyle : {})
              }}
              aria-pressed={layout === "landscape"}
            >
              Landscape
            </button>
          </div>

          <div style={controlGroupStyle}>
            <button
              type="button"
              onClick={handleFullscreen}
              style={secondaryButtonStyle}
            >
              Fullscreen
            </button>

            <button
              type="button"
              onClick={handleSaveImage}
              style={{
                ...secondaryButtonStyle,
                opacity: isSavingImage ? 0.7 : 1,
                cursor: isSavingImage ? "not-allowed" : "pointer"
              }}
              disabled={isSavingImage}
            >
              {isSavingImage ? "Saving..." : "Save Image"}
            </button>
          </div>

          {exportError && <p style={exportErrorStyle}>{exportError}</p>}
        </div>
      )}

      <div
        ref={displayRef}
        style={{
          ...previewRootStyle,
          backgroundColor: activeTheme.previewBackground
        }}
      >
        <div
          ref={previewViewportRef}
          aria-busy={!isDisplayReady}
          style={{
            ...previewViewportStyle,
            ...(isFullscreen ? fullscreenPreviewViewportStyle : {})
          }}
        >
          {!isDisplayReady && <div style={{ ...previewLoadingStyle, aspectRatio: `${boardWidth} / ${boardHeight}` }}><span>Preparing Show Display...</span></div>}
          <div
            style={{
              ...scaleShellStyle,
              width: `${boardWidth * previewScale}px`,
              height: `${boardHeight * previewScale}px`,
              opacity: isDisplayReady ? 1 : 0,
              visibility: isDisplayReady ? "visible" : "hidden",
              position: isDisplayReady ? "relative" : "absolute"
            }}
          >
            <article
              ref={boardRef}
              style={{
                ...displayCardStyle,
                ...(isLandscape ? landscapeCardStyle : portraitCardStyle),
                width: `${boardWidth}px`,
                height: `${boardHeight}px`,
                minWidth: `${boardWidth}px`,
                minHeight: `${boardHeight}px`,
                transform: `scale(${previewScale})`,
                backgroundColor: activeTheme.cardBackground,
                color: activeTheme.textColor,
                borderColor: activeTheme.exportBorderColor
              }}
            >
              {isLandscape ? (
                <>
                  <section style={landscapeMediaColumnStyle}>
                    {heroContent}

                    <section
                      style={{
                        ...landscapeQrFooterStyle,
                        borderColor: activeTheme.sectionBorderColor
                      }}
                    >
                      {footerContent}
                    </section>
                  </section>

                  <section style={landscapeInfoColumnStyle}>
                    <header style={landscapeDisplayHeaderStyle}>
                      <h1 style={landscapeVehicleTitleStyle}>{vehicleTitle}</h1>
                    </header>

                    <div style={landscapeInfoStackStyle}>
                      {specsSection}
                      {modBreakdownSection}
                    </div>
                  </section>
                </>
              ) : (
                <>
                  {heroContent}

                  <section style={contentSectionStyle}>
                    <header style={displayHeaderStyle}>
                      <h1 style={vehicleTitleStyle}>{vehicleTitle}</h1>

                      {subtitle && (
                        <p
                          style={{
                            ...subtitleStyle,
                            color: activeTheme.mutedTextColor
                          }}
                        >
                          {subtitle}
                        </p>
                      )}
                    </header>

                    <div style={infoStackStyle}>
      
                      {specsSection}
                      {modBreakdownSection}
                    </div>
                  </section>

                  <section
                    style={{
                      ...qrFooterStyle,
                      borderColor: activeTheme.sectionBorderColor
                    }}
                  >
                    {footerContent}
                  </section>
                </>
              )}
            </article>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}

const waitForNextFrame = () =>
  new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    });
  });

const withTimeout = (promise, timeoutMs, message) => {
  let timeoutId;

  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    clearTimeout(timeoutId);
  });
};

const waitForDocumentFonts = async () => {
  if (!document.fonts?.ready) return;

  try {
    await withTimeout(
      document.fonts.ready,
      5000,
      "Font loading timed out."
    );
  } catch (error) {
    console.warn("Continuing export before all fonts reported ready:", error);
  }
};

const waitForBoardImages = async (container) => {
  const images = Array.from(container.querySelectorAll("img"));

  await Promise.all(
    images.map(
      (image) =>
        new Promise((resolve, reject) => {
          if (image.complete) {
            if (image.naturalWidth > 0) {
              resolve();
            } else {
              reject(new Error("An image could not be loaded for export."));
            }

            return;
          }

          let timeoutId;

          const cleanup = () => {
            clearTimeout(timeoutId);
            image.removeEventListener("load", handleLoad);
            image.removeEventListener("error", handleError);
          };

          const handleLoad = () => {
            cleanup();

            if (image.naturalWidth > 0) {
              resolve();
            } else {
              reject(new Error("An image could not be loaded for export."));
            }
          };

          const handleError = () => {
            cleanup();
            reject(new Error("An image could not be loaded for export."));
          };

          timeoutId = setTimeout(() => {
            cleanup();
            reject(new Error("Image loading timed out."));
          }, 8000);

          image.addEventListener("load", handleLoad);
          image.addEventListener("error", handleError);
        })
    )
  );
};

const downloadDataUrl = (dataUrl, fileName) => {
  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const getSafeFileName = (value) => {
  const safeValue = String(value || "show-display")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return safeValue || "show-display";
};

const getShowDisplayStamp = (username, date = new Date()) => {
  const displayUsername = String(username || "")
    .trim()
    .toUpperCase() || "MYBUILDID";
  const week = getIsoWeekNumber(date);
  const paddedWeek = String(week).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);
  const fullYear = date.getFullYear();

  return {
    text: `${displayUsername} • ${paddedWeek} • ${year}`,
    ariaLabel: `Show display stamp for ${displayUsername}, week ${paddedWeek} of ${fullYear}`,
    title: `Generated week ${paddedWeek} of ${fullYear}`
  };
};

const getIsoWeekNumber = (date) => {
  const utcDate = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  );
  const day = utcDate.getUTCDay() || 7;

  utcDate.setUTCDate(utcDate.getUTCDate() + 4 - day);

  const yearStart = new Date(Date.UTC(utcDate.getUTCFullYear(), 0, 1));

  return Math.ceil(((utcDate - yearStart) / 86400000 + 1) / 7);
};

const getVehicleTitle = (car) => {
  const safeCar = car || {};

  return (
    safeCar.name ||
    [safeCar.year, safeCar.make, safeCar.model].filter(Boolean).join(" ").trim() ||
    "Vehicle"
  );
};

const getVehicleSubtitle = (car, title) => {
  const safeCar = car || {};
  const generatedSubtitle = [safeCar.year, safeCar.make, safeCar.model]
    .filter(Boolean)
    .join(" ")
    .trim();

  if (!generatedSubtitle) return "";

  if (
    generatedSubtitle.trim().toLowerCase() ===
    String(title || "").trim().toLowerCase()
  ) {
    return "";
  }

  return generatedSubtitle;
};


const truncateText = (text, maxLength) => {
  if (!text || text.length <= maxLength) return text;

  const trimmed = text.slice(0, maxLength).trim();
  const lastSpace = trimmed.lastIndexOf(" ");

  if (lastSpace > 220) {
    return `${trimmed.slice(0, lastSpace).trim()}...`;
  }

  return `${trimmed}...`;
};

const getVehicleSpecComparisonData = (car, currentPhase) => {
  if (!car) {
    return {
      rows: []
    };
  }

  const normalizedCar = normalizeCarStages(car);
  const resolvedPhase = currentPhase || getCurrentStage(normalizedCar);
  const oem = normalizeVehicleSpecs(normalizedCar.oemSpecs);
  const current = normalizeVehicleSpecs(resolvedPhase?.specs);

  const rows = VEHICLE_SPEC_FIELDS.map(({ key, label }) => ({
    key,
    label,
    oemValue: oem[key],
    currentValue: current[key]
  }))
    .filter((row) => row.oemValue || row.currentValue)
    .slice(0, 8);

  return {
    rows
  };
};

const getModCategoryBreakdown = (modsSource) => {
  const mods = normalizeMods(modsSource);
  const categoryCounts = {};

  mods.forEach((mod) => {
    const category = normalizeCategoryLabel(mod.category || "Other");

    if (shouldExcludeModCategory(category)) return;

    categoryCounts[category] = (categoryCounts[category] || 0) + 1;
  });

  const entries = Object.entries(categoryCounts).map(([category, count]) => ({
    category,
    count
  }));

  if (entries.length === 0) return [];

  const sorted = entries.sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    return a.category.localeCompare(b.category);
  });

  const topEntries = sorted.slice(0, 5);
  const remainingEntries = sorted.slice(5);

  if (remainingEntries.length > 0) {
    topEntries.push({
      category: "Other",
      count: remainingEntries.reduce((total, item) => total + item.count, 0)
    });
  }

  return applyRoundedPercentages(topEntries);
};

const normalizeMods = (modsSource) => {
  if (Array.isArray(modsSource)) {
    return modsSource.filter(isMeaningfulMod);
  }

  if (modsSource && typeof modsSource === "object") {
    return Object.entries(modsSource).flatMap(([category, value]) => {
      if (!Array.isArray(value)) return [];

      return value
        .filter(isMeaningfulMod)
        .map((item) => ({
          ...item,
          category: item.category || category
        }));
    });
  }

  return [];
};

const isMeaningfulMod = (mod) => {
  if (!mod || typeof mod !== "object") return false;

  return Object.values(mod).some((value) => {
    if (value === null || value === undefined) return false;
    return String(value).trim() !== "";
  });
};

const normalizeCategoryLabel = (category) => {
  const value = String(category || "Other").trim().toLowerCase();

  if (
    [
      "wheel & tire",
      "wheels & tires",
      "wheel and tire",
      "wheels and tires",
      "wheels/tires"
    ].includes(value)
  ) {
    return "Wheel & Tire";
  }

  if (value === "performance mods" || value === "engine") return "Performance";
  if (value === "suspension mods") return "Suspension";
  if (value === "exterior mods") return "Exterior";
  if (value === "interior mods") return "Interior";
  if (value === "misc" || value === "miscellaneous") return "Miscellaneous";

  return String(category || "Other").trim() || "Other";
};

const shouldExcludeModCategory = (category) => {
  const value = String(category || "").trim().toLowerCase();

  return ["maintenance", "service", "warranty", "documents"].includes(value);
};

const applyRoundedPercentages = (items) => {
  const total = items.reduce((sum, item) => sum + item.count, 0);

  if (total <= 0) return [];

  const withRaw = items.map((item) => {
    const raw = (item.count / total) * 100;
    const floored = Math.floor(raw);

    return {
      ...item,
      raw,
      percentage: floored,
      remainder: raw - floored
    };
  });

  let remaining = 100 - withRaw.reduce((sum, item) => sum + item.percentage, 0);

  return withRaw
    .sort((a, b) => b.remainder - a.remainder)
    .map((item) => {
      if (remaining > 0) {
        remaining -= 1;
        return {
          ...item,
          percentage: item.percentage + 1
        };
      }

      return item;
    })
    .sort((a, b) => {
      if (b.percentage !== a.percentage) return b.percentage - a.percentage;
      return a.category.localeCompare(b.category);
    })
    .map(({ category, count, percentage }) => ({
      category,
      count,
      percentage
    }));
};

const darkTheme = {
  previewBackground: "#0b0b0b",
  cardBackground: "#111111",
  textColor: "#ffffff",
  bodyTextColor: "#e7e7e7",
  mutedTextColor: "#9a9a9a",
  oemTextColor: "#b8b8b8",
  accentColor: "#e10600",
  borderColor: "#2a2a2a",
  exportBorderColor: "rgba(255,255,255,0.08)",
  sectionBorderColor: "#2b2b2b",
  trackColor: "#242424",
  secondaryBarColor: "#777",
  stampBackground: "rgba(255,255,255,0.06)",
  stampBorderColor: "rgba(255,255,255,0.16)",
  stampTextColor: "#d8d8d8"
};

const lightTheme = {
  previewBackground: "#e9edf4",
  cardBackground: "#ffffff",
  textColor: "#111111",
  bodyTextColor: "#262626",
  mutedTextColor: "#666666",
  oemTextColor: "#777777",
  accentColor: "#e10600",
  borderColor: "#d7d7d7",
  exportBorderColor: "rgba(0,0,0,0.08)",
  sectionBorderColor: "#e5e5e5",
  trackColor: "#ececec",
  secondaryBarColor: "#8a8a8a",
  stampBackground: "rgba(0,0,0,0.045)",
  stampBorderColor: "rgba(0,0,0,0.14)",
  stampTextColor: "#555555"
};

const controlsStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: "10px",
  justifyContent: "center",
  alignItems: "center",
  margin: "0 0 18px"
};

const controlGroupStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
  justifyContent: "center"
};

const controlButtonStyle = {
  backgroundColor: "#181818",
  color: "white",
  border: "1px solid #333",
  borderRadius: "999px",
  padding: "10px 16px",
  fontWeight: "800",
  cursor: "pointer"
};

const activeControlButtonStyle = {
  backgroundColor: "#e10600",
  borderColor: "#e10600"
};

const secondaryButtonStyle = {
  ...controlButtonStyle,
  backgroundColor: "#242424"
};

const exportErrorStyle = {
  width: "100%",
  margin: "2px 0 0",
  color: "#ffb4b4",
  textAlign: "center",
  fontSize: "13px",
  fontWeight: "700"
};

const previewRootStyle = {
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  borderRadius: "28px",
  padding: "16px",
  boxSizing: "border-box",
  overflow: "hidden"
};

const previewViewportStyle = {
  position: "relative",
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  overflow: "hidden",
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  boxSizing: "border-box"
};

const previewLoadingStyle = {
  width: "100%",
  maxWidth: "850px",
  minHeight: "220px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1px solid rgba(255,255,255,.1)",
  borderRadius: "20px",
  background: "rgba(0,0,0,.14)",
  color: "rgba(255,255,255,.72)",
  fontSize: "14px",
  fontWeight: "800"
};

const fullscreenPreviewViewportStyle = {
  minHeight: "100vh",
  alignItems: "center"
};

const scaleShellStyle = {
  position: "relative",
  flexShrink: 0,
  margin: "0 auto",
  overflow: "visible"
};

const displayCardStyle = {
  boxSizing: "border-box",
  border: "2px solid",
  borderRadius: "24px",
  boxShadow: "0 20px 50px rgba(0,0,0,0.28)",
  overflow: "hidden",
  flexShrink: 0,
  maxWidth: "none",
  transformOrigin: "top left"
};

const portraitCardStyle = {
  padding: "22px",
  display: "grid",
  gridTemplateColumns: "1fr",
  gridTemplateRows: "245px 1fr 180px",
  gap: "10px"
};

const landscapeCardStyle = {
  padding: "28px",
  display: "grid",
  gridTemplateColumns: "500px 1fr",
  gap: "42px",
  alignItems: "stretch"
};

const landscapeMediaColumnStyle = {
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  gap: "39px"
};
const landscapeInfoColumnStyle = {
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: "18px"
};

const heroSectionStyle = {
  minWidth: 0
};

const heroImageStyle = {
  width: "100%",
  height: "245px",
  objectFit: "cover",
  borderRadius: "18px",
  display: "block"
};

const landscapeHeroImageStyle = {
  height: "330px",
  objectFit: "cover"
};

const heroPlaceholderStyle = {
  width: "100%",
  height: "245px",
  borderRadius: "18px",
  border: "1px solid",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  fontSize: "28px",
  fontWeight: "900",
  backgroundColor: "#181818"
};

const contentSectionStyle = {
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: "8px"
};

const displayHeaderStyle = {
  textAlign: "center"
};

const landscapeDisplayHeaderStyle = {
  textAlign: "center",
  marginTop: "0"
};

const vehicleTitleStyle = {
  margin: "0",
  fontSize: "42px",
  lineHeight: "0.98",
  fontWeight: "900",
  letterSpacing: "0"
};

const landscapeVehicleTitleStyle = {
  margin: "0 0 18px",
  fontSize: "32px",
  lineHeight: "1",
  fontWeight: "900",
  letterSpacing: "-0.02em",
  textAlign: "center",
  overflowWrap: "break-word"
};

const subtitleStyle = {
  margin: "7px 0 0",
  fontSize: "16px",
  fontWeight: "700"
};

const infoStackStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "10px"
};

const landscapeInfoStackStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "18px"
};

const displaySectionStyle = {
  borderTop: "1px solid",
  paddingTop: "10px"
};

const landscapeFirstRightSectionStyle = {
  borderTop: "1px solid",
  paddingTop: "16px"
};

const sectionTitleStyle = {
  margin: "0 0 8px",
  fontSize: "15px",
  fontWeight: "900",
  letterSpacing: "1.3px",
  textTransform: "uppercase"
};

const specTableStyle = {
  display: "flex",
  flexDirection: "column"
};

const portraitSpecGridStyle = {
  gridTemplateColumns: "150px 1fr 1.25fr"
};

const landscapeSpecGridStyle = {
  gridTemplateColumns: "150px 1fr 1.25fr"
};

const specHeaderRowStyle = {
  display: "grid",
  gap: "12px",
  padding: "0 0 8px",
  borderBottom: "1px solid",
  fontSize: "11px",
  fontWeight: "900",
  letterSpacing: "0.9px",
  textTransform: "uppercase"
};

const specRowStyle = {
  display: "grid",
  gap: "12px",
  padding: "8px 0",
  borderBottom: "1px solid",
  alignItems: "start"
};

const specLabelStyle = {
  fontSize: "12px",
  fontWeight: "800",
  textTransform: "uppercase",
  wordBreak: "normal",
  overflowWrap: "break-word"
};

const specOemValueStyle = {
  fontSize: "14px",
  lineHeight: "1.3",
  fontWeight: "650",
  wordBreak: "normal",
  overflowWrap: "break-word"
};

const specCurrentValueStyle = {
  fontSize: "14px",
  lineHeight: "1.3",
  fontWeight: "850",
  wordBreak: "normal",
  overflowWrap: "break-word"
};

const breakdownStackStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "8px"
};

const breakdownRowStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "5px"
};

const breakdownLabelRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: "12px",
  fontSize: "14px",
  fontWeight: "800"
};

const breakdownTrackStyle = {
  height: "8px",
  borderRadius: "999px",
  overflow: "hidden"
};

const breakdownFillStyle = {
  height: "100%",
  borderRadius: "999px"
};

const qrFooterStyle = {
  width: "100%",
  borderTop: "none",
  paddingTop: "0px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  boxSizing: "border-box"
};

const landscapeQrFooterStyle = {
  width: "100%",
  marginTop: "0",
  padding: "14px 10px 0",
  borderTop: "1px solid",
  boxSizing: "border-box"
};

const footerGroupStyle = {
  width: "620px",
  margin: "0 auto",
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  alignItems: "center",
  justifyItems: "center",
  columnGap: "36px",
  boxSizing: "border-box"
};

const landscapeFooterGroupStyle = {
  width: "100%",
  maxWidth: "470px",
  margin: "0 auto",
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  alignItems: "center",
  justifyItems: "center",
  gap: "18px",
  boxSizing: "border-box"
};

const footerBrandColumnStyle = {
  width: "100%",
  minWidth: 0,
  minHeight: "175px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  boxSizing: "border-box"
};

const footerQrColumnStyle = {
  width: "100%",
  minWidth: 0,
  minHeight: "175px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  boxSizing: "border-box"
};

const landscapeFooterBrandColumnStyle = {
  width: "100%",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  boxSizing: "border-box"
};

const landscapeFooterQrColumnStyle = {
  width: "100%",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  boxSizing: "border-box"
};

const footerLogoStyle = {
  width: "190px",
  height: "auto",
  display: "block"
};

const landscapeFooterLogoStyle = {
  width: "170px",
  height: "auto",
  display: "block"
};

const footerUrlStyle = {
  margin: "5px 0 0",
  fontSize: "11px",
  lineHeight: "1.15",
  fontWeight: "800",
  letterSpacing: "0.5px"
};

const footerStampStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  marginTop: "7px",
  padding: "5px 10px",
  borderRadius: "999px",
  border: "1px solid",
  fontSize: "9px",
  lineHeight: 1,
  fontWeight: "900",
  letterSpacing: "1.2px",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
  fontFamily: "\"Courier New\", Courier, monospace",
  opacity: 0.82
};

const footerEyebrowStyle = {
  width: "100%",
  maxWidth: "176px",
  margin: "5px auto 0",
  fontSize: "9px",
  lineHeight: "1.1",
  fontWeight: "950",
  letterSpacing: "0.6px",
  textTransform: "uppercase",
  textAlign: "center"
};

const qrPanelStyle = {
  backgroundColor: "#ffffff",
  borderRadius: "16px",
  padding: "8px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center"
};

const stateCardStyle = {
  color: "white",
  textAlign: "center",
  padding: "40px",
  backgroundColor: "#181818",
  border: "1px solid #2a2a2a",
  borderRadius: "18px"
};

export default ShowDisplay;
